import { useState, useMemo } from "react";
import PricingDialog from "@/components/PricingDialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Lock, Bug, Calendar, TrendingUp, Server } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

interface Vulnerability {
  cveID: string;
  vendor: string;
  product: string;
  name: string;
  description: string;
  dateAdded: string;
  dueDate: string;
  action: string;
  knownRansomware: string;
  notes: string;
}

// Known state-attributed cyber groups mapped to vendors/keywords
const STATE_ATTRIBUTION: Record<string, { actor: string; flag: string }> = {
  "Microsoft": { actor: "Multiple (APT28, APT29, Lazarus)", flag: "🇷🇺🇰🇵🇨🇳" },
  "Apple": { actor: "NSO Group / State clients", flag: "🇮🇱" },
  "Google": { actor: "Multiple APTs", flag: "🇨🇳🇷🇺" },
  "Cisco": { actor: "APT groups (CN/RU)", flag: "🇨🇳🇷🇺" },
  "Fortinet": { actor: "APT (CN/IR)", flag: "🇨🇳🇮🇷" },
  "Adobe": { actor: "Multiple", flag: "🇨🇳🇷🇺" },
  "Ivanti": { actor: "UNC5221 (CN)", flag: "🇨🇳" },
  "Palo Alto Networks": { actor: "APT groups", flag: "🇨🇳🇰🇵" },
  "VMware": { actor: "APT29 (RU)", flag: "🇷🇺" },
  "Citrix": { actor: "Multiple APTs", flag: "🇨🇳🇷🇺🇮🇷" },
  "SonicWall": { actor: "UNC groups", flag: "🇨🇳" },
  "Zyxel": { actor: "Sandworm (RU)", flag: "🇷🇺" },
  "MOVEit": { actor: "Cl0p (RU-linked)", flag: "🇷🇺" },
  "Progress": { actor: "Cl0p (RU-linked)", flag: "🇷🇺" },
  "Barracuda": { actor: "UNC4841 (CN)", flag: "🇨🇳" },
};

const CyberAttackMonitor = () => {
  const [isPremium] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"timeline" | "table">("timeline");

  const { data, isLoading } = useQuery({
    queryKey: ["cisa-kev"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-cisa-kev");
      if (error) throw error;
      return data as {
        title: string;
        catalogVersion: string;
        dateReleased: string;
        count: number;
        vulnerabilities: Vulnerability[];
      };
    },
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const vulnerabilities = data?.vulnerabilities || [];
  const freeLimit = 15;
  const visible = isPremium ? vulnerabilities : vulnerabilities.slice(0, freeLimit);

  // Timeline chart: count by month (last 12 months)
  const timelineData = useMemo(() => {
    if (!vulnerabilities.length) return [];
    const months: Record<string, number> = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months[key] = 0;
    }
    vulnerabilities.forEach((v) => {
      const key = v.dateAdded?.substring(0, 7);
      if (key && months[key] !== undefined) months[key]++;
    });
    return Object.entries(months).map(([month, count]) => ({
      month: month.substring(5) + "/" + month.substring(2, 4),
      count,
    }));
  }, [vulnerabilities]);

  // Top targeted vendors
  const topVendors = useMemo(() => {
    const counts: Record<string, number> = {};
    vulnerabilities.forEach((v) => {
      counts[v.vendor] = (counts[v.vendor] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [vulnerabilities]);

  // Ransomware stats
  const ransomwareCount = vulnerabilities.filter((v) => v.knownRansomware === "Known").length;

  const getAttribution = (vendor: string) => STATE_ATTRIBUTION[vendor] || null;

  return (
    <section className="py-8 md:py-12">
      <div className="container mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
            <ShieldAlert className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Cyber Attack Monitor</h2>
            <p className="text-sm text-muted-foreground">
              CISA Known Exploited Vulnerabilities — {data?.count || "..."} total CVEs tracked
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Bug className="h-3.5 w-3.5" /> Latest CVEs
              </div>
              <p className="text-2xl font-bold text-foreground">{vulnerabilities.length}</p>
              <p className="text-[10px] text-muted-foreground">last 100 entries</p>
            </CardContent>
          </Card>
          <Card className="border-destructive/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <ShieldAlert className="h-3.5 w-3.5" /> Ransomware-linked
              </div>
              <p className="text-2xl font-bold text-destructive">{ransomwareCount}</p>
              <p className="text-[10px] text-muted-foreground">known campaigns</p>
            </CardContent>
          </Card>
          <Card className="border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Server className="h-3.5 w-3.5" /> Top Target
              </div>
              <p className="text-lg font-bold text-foreground">{topVendors[0]?.[0] || "—"}</p>
              <p className="text-[10px] text-muted-foreground">{topVendors[0]?.[1] || 0} CVEs</p>
            </CardContent>
          </Card>
          <Card className="border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Calendar className="h-3.5 w-3.5" /> Catalog Updated
              </div>
              <p className="text-sm font-bold text-foreground">
                {data?.dateReleased ? new Date(data.dateReleased).toLocaleDateString() : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground">v{data?.catalogVersion || "..."}</p>
            </CardContent>
          </Card>
        </div>

        {/* Timeline Chart */}
        <Card className="border-primary/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5" /> CVE Addition Timeline (12 months)
              </CardTitle>
              <div className="flex gap-1">
                <Button
                  variant={viewMode === "timeline" ? "default" : "outline"}
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setViewMode("timeline")}
                >
                  Chart
                </Button>
                <Button
                  variant={viewMode === "table" ? "default" : "outline"}
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setViewMode("table")}
                >
                  Table
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : viewMode === "timeline" ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} name="New CVEs" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="relative">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">CVE ID</TableHead>
                      <TableHead className="text-xs">Vendor / Product</TableHead>
                      <TableHead className="text-xs">Attribution</TableHead>
                      <TableHead className="text-xs">Ransomware</TableHead>
                      <TableHead className="text-xs">Added</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visible.map((v) => {
                      const attr = getAttribution(v.vendor);
                      return (
                        <TableRow key={v.cveID}>
                          <TableCell className="text-xs font-mono font-medium text-foreground">{v.cveID}</TableCell>
                          <TableCell className="text-xs">
                            <span className="font-medium text-foreground">{v.vendor}</span>
                            <span className="text-muted-foreground"> / {v.product}</span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {attr ? (
                              <span>
                                {attr.flag}{" "}
                                <span className="text-muted-foreground">{attr.actor}</span>
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {v.knownRansomware === "Known" ? (
                              <Badge className="bg-destructive/20 text-destructive border-destructive/30 text-[10px]">
                                Known
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">No</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {v.dateAdded ? new Date(v.dateAdded).toLocaleDateString() : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* Premium gate */}
                {!isPremium && vulnerabilities.length > freeLimit && (
                  <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-card to-transparent flex items-end justify-center pb-4">
                    <Button size="sm" className="gap-1.5" onClick={() => setPricingOpen(true)}>
                      <Lock className="h-3.5 w-3.5" />
                      Unlock all {data?.count || vulnerabilities.length} CVEs — Go Premium
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Targeted Vendors */}
        <Card className="border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Most Targeted Vendors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {topVendors.map(([vendor, count]) => {
                const attr = getAttribution(vendor);
                return (
                  <div key={vendor} className="rounded-lg border border-border p-3">
                    <p className="text-xs font-medium text-foreground">{vendor}</p>
                    <p className="text-lg font-bold text-foreground">{count}</p>
                    {attr && (
                      <p className="text-[10px] text-muted-foreground">{attr.flag} {attr.actor.split("(")[0]}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <p className="text-[10px] text-muted-foreground text-center">
          Data source: CISA Known Exploited Vulnerabilities Catalog. Attribution is estimated based on public threat intelligence reports.
        </p>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>
  );
};

export default CyberAttackMonitor;
